import { kisiBasiHesapla } from '../../../api/src/modules/hizli-teklif/kisi-basi-hesap';
import type { PaketDuzenlemesi } from '../../../api/src/modules/hizli-teklif/paket-duzenleme-tipleri';
import type { AracSecimi, PaketFiyati, PaketKodu } from './tipler';
import { KATALOG_KODU } from './sozlesme';
import { paraYaz, tutarCoz } from './mantik';
export type { PaketDuzenlemesi };
export interface PaketDuzenlemeTaslagi {
  readonly paraBirimi: 'USD' | 'EUR' | 'AED';
  readonly ad?: string;
  readonly yetiskin?: string;
  readonly saat?: string;
  readonly yatBoyutu?: string;
  readonly aracToplamlari?: Readonly<Partial<Record<AracSecimi, string>>>;
}
export type PaketDuzenlemeTaslaklari = Readonly<Partial<Record<PaketKodu, PaketDuzenlemeTaslagi>>>;
export function paketDuzenlemesiCoz(kod: PaketKodu, d: PaketDuzenlemeTaslagi): PaketDuzenlemesi | null {
  const amount = (s: string) => { const n = tutarCoz(s); return n !== null && n > 0 && n <= 100_000_000 ? n : null; };
  const yetiskin = d.yetiskin === undefined ? undefined : amount(d.yetiskin);
  const saat = d.saat === undefined ? undefined : amount(d.saat);
  const arac = Object.fromEntries(Object.entries(d.aracToplamlari ?? {}).map(([k, v]) => [k, amount(v)]));
  // eslint-disable-next-line no-control-regex -- reject control characters in authored titles
  if (yetiskin === null || saat === null || Object.values(arac).some(n => n === null) || (d.ad !== undefined && (d.ad.trim().length < 2 || d.ad.length > 120 || /[<>\u0000-\u001f]/.test(d.ad)))) return null;
  return { kod: KATALOG_KODU[kod], paraBirimi: d.paraBirimi,
    ...(d.ad === undefined ? {} : { ad: d.ad.trim() }), ...(yetiskin === undefined ? {} : { yetiskinMinor: yetiskin }),
    ...(saat === undefined ? {} : { saatBasiMinor: saat, yatBoyutu: d.yatBoyutu }),
    ...(Object.keys(arac).length === 0 ? {} : { aracToplamlari: arac as Record<AracSecimi, number> }),
  };
}
/** Only the hourly price belongs to a yacht size; other product edits share the quote context. */
export function paketDuzenlemesiniSinirla(d: PaketDuzenlemeTaslagi, f: PaketFiyati): PaketDuzenlemeTaslagi {
  if (!f.yat || d.saat === undefined || d.yatBoyutu === f.yat.boyut) return d;
  return { paraBirimi: d.paraBirimi, ...(d.ad === undefined ? {} : { ad: d.ad }),
    ...(d.yetiskin === undefined ? {} : { yetiskin: d.yetiskin }),
    ...(d.aracToplamlari === undefined ? {} : { aracToplamlari: d.aracToplamlari }),
  };
}
export function paketFiyatiniDuzenle(f: PaketFiyati, d: PaketDuzenlemeTaslagi | undefined, yetiskin: number, yaslar: readonly number[], safariBebekKoltukSayisi = 0): PaketFiyati {
  if (!d || d.paraBirimi !== f.paraBirimi) return f;
  const v = paketDuzenlemesiCoz(f.kod, paketDuzenlemesiniSinirla(d, f));
  if (!v) return f;
  const kisi = v.yetiskinMinor === undefined ? f.kisiBasiFiyat : { ...f.kisiBasiFiyat, yetiskinMinor: v.yetiskinMinor };
  const yat = f.yat && v.saatBasiMinor !== undefined && f.yat.boyut === v.yatBoyutu ? { ...f.yat, saatBasiMinor: v.saatBasiMinor } : f.yat;
  const kisiHesabi = kisi === undefined ? undefined : kisiBasiHesapla(kisi, yetiskin, yaslar, d.paraBirimi, safariBebekKoltukSayisi);
  return { ...f, ad: v.ad ?? f.ad,
    kapsam: f.kisiBasi && v.yetiskinMinor !== undefined ? kisiHesabi!.dayanak : yat && v.saatBasiMinor !== undefined ? `${yat.saat} saat x ${paraYaz(yat.saatBasiMinor, d.paraBirimi)} (yat başı)` : f.kapsam,
    ...(kisi === undefined ? {} : { kisiBasiFiyat: kisi }), ...(yat === undefined ? {} : { yat }),
    tutarMinor: f.kisiBasi && v.yetiskinMinor !== undefined ? kisiHesabi!.toplam
      : yat && v.saatBasiMinor !== undefined ? yat.saat * yat.saatBasiMinor : f.tutarMinor,
    ...(f.secenekler === undefined ? {} : { secenekler: f.secenekler.map(s => ({ ...s, tutarMinor: v.aracToplamlari?.[s.anahtar] ?? s.tutarMinor, aciklama: v.aracToplamlari?.[s.anahtar] === undefined ? s.aciklama : `${s.aciklama.replace(/\s*\([^)]*\)\s*$/, '')} (teklife özel toplam)` })) }),
  };
}
